import DeleteIcon from "@mui/icons-material/Delete";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

interface AudioRecorderAnswerProps {
  onAudioRecorded: (audioBlob: Blob) => void;
}

const AudioRecorderAnswer: React.FC<AudioRecorderAnswerProps> = ({
  onAudioRecorded,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");

  // Limite máximo de gravação em segundos (5 minutos = 300 segundos)
  const MAX_RECORDING_TIME = 300;

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const playbackTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const getAudioDevices = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });

        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputDevices = devices.filter(
          (device) => device.kind === "audioinput"
        );

        setAudioDevices(audioInputDevices);
        if (audioInputDevices.length > 0)
          setSelectedDeviceId(audioInputDevices[0].deviceId);
      } catch (err) {
        console.error("Erro ao acessar dispositivos de mídia:", err);
        setError(
          "Não foi possível acessar seus dispositivos de áudio. Verifique as permissões do microfone no seu navegador."
        );
      }
    };

    getAudioDevices();
  }, []);

  useEffect(() => {
    audioRef.current = new Audio();

    const audio = audioRef.current;
    const setupAudioEvents = () => {
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("ended", handleAudioEnded);
      audio.addEventListener("canplaythrough", handleCanPlayThrough);
    };

    setupAudioEvents();

    return () => {
      stopAudio();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (playbackTimerRef.current) clearInterval(playbackTimerRef.current);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);

      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleAudioEnded);
      audio.removeEventListener("canplaythrough", handleCanPlayThrough);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isRecording && recordingTime >= MAX_RECORDING_TIME) {
      stopRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingTime, isRecording]);

  const handleTimeUpdate = () => {
    if (audioRef.current && !isNaN(audioRef.current.currentTime)) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current && !isNaN(audioRef.current.duration)) {
      if (audioRef.current.duration === Infinity) {
        setDuration(audioBlob ? audioBlob.size / 16000 : recordingTime);
      } else {
        setDuration(audioRef.current.duration);
      }
      setAudioLoaded(true);
    }
  };

  const handleCanPlayThrough = () => {
    if (
      audioRef.current &&
      !isNaN(audioRef.current.duration) &&
      audioRef.current.duration !== Infinity
    ) {
      setDuration(audioRef.current.duration);
      setAudioLoaded(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
    }
  };

  const startRecording = async () => {
    if (!selectedDeviceId) {
      setError("Nenhum dispositivo de gravação selecionado.");
      return;
    }
    setError(null);

    try {
      const constraints = {
        audio: { deviceId: { exact: selectedDeviceId } },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        onAudioRecorded(blob);
        setAudioLoaded(false);

        if (audioRef.current) {
          if (audioRef.current.src) {
            URL.revokeObjectURL(audioRef.current.src);
          }
          const audioURL = URL.createObjectURL(blob);
          audioRef.current.src = audioURL;
          audioRef.current.load();
        }

        updateDurationWithAudioContext(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      mediaRecorderRef.current.start();
    } catch (error) {
      console.error("Erro ao iniciar a gravação:", error);
      setError(
        "Falha ao iniciar a gravação. O dispositivo pode estar em uso ou desconectado."
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const playAudio = () => {
    if (audioRef.current && audioBlob) {
      audioRef.current
        .play()
        .catch((err) => console.error("Erro ao reproduzir áudio:", err));
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  const deleteRecording = () => {
    stopAudio();
    setAudioBlob(null);
    setDuration(0);
    setCurrentTime(0);
    setAudioLoaded(false);
    onAudioRecorded(new Blob());
  };

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    const newTime = newValue as number;
    if (audioRef.current && audioLoaded && isFinite(newTime)) {
      setCurrentTime(newTime);
      audioRef.current.currentTime = newTime;
    }
  };

  const updateDurationWithAudioContext = (blob: Blob) => {
    const reader = new FileReader();
    reader.onload = () => {
      const audioData = reader.result as ArrayBuffer;
      const AudioContext =
        window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContext();
      audioContext
        .decodeAudioData(audioData)
        .then((buffer) => {
          setDuration(buffer.duration);
          setAudioLoaded(true);
        })
        .catch((error) => {
          console.error("Erro ao decodificar áudio:", error);
          setDuration(recordingTime);
          setAudioLoaded(true);
        });
    };
    reader.readAsArrayBuffer(blob);
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || !isFinite(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const recordingTimePercent = (recordingTime / MAX_RECORDING_TIME) * 100;
  const sliderMax = audioLoaded
    ? duration
    : recordingTime > 0
    ? recordingTime
    : 100;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 500,
        mt: { xs: 2, sm: 3 },
        mx: "auto",
        px: { xs: 2, sm: 0 },
      }}
    >
      {!audioBlob ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <FormControl
            fullWidth
            sx={{ mb: 2 }}
            disabled={isRecording || audioDevices.length === 0}
          >
            <InputLabel id="audio-device-select-label">
              Dispositivo de Gravação
            </InputLabel>
            <Select
              labelId="audio-device-select-label"
              value={selectedDeviceId}
              label="Dispositivo de Gravação"
              onChange={(e) => setSelectedDeviceId(e.target.value)}
            >
              {audioDevices.length > 0 ? (
                audioDevices.map((device) => (
                  <MenuItem key={device.deviceId} value={device.deviceId}>
                    {device.label ||
                      `Microfone ${audioDevices.indexOf(device) + 1}`}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>Nenhum microfone encontrado</MenuItem>
              )}
            </Select>
          </FormControl>

          {error && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mb: 2, textAlign: "center" }}
            >
              {error}
            </Typography>
          )}

          {/* AJUSTE RESPONSIVO: Centraliza o texto e ajusta o tamanho da fonte */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              textAlign: "center",
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            {isRecording
              ? `Gravando... ${formatTime(recordingTime)} / ${formatTime(
                  MAX_RECORDING_TIME
                )}`
              : "Clique para iniciar a gravação (máximo 5 minutos)"}
          </Typography>

          {isRecording && (
            <Typography variant="caption" sx={{ mb: 2 }}>
              {`${formatTime(recordingTime)} / ${formatTime(
                MAX_RECORDING_TIME
              )}`}
            </Typography>
          )}

          {isRecording && (
            <Box sx={{ width: "100%", mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={recordingTimePercent}
                color={recordingTimePercent > 90 ? "error" : "primary"}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
          )}

          {isRecording ? (
            <IconButton
              onClick={stopRecording}
              color="error"
              sx={{
                width: 64,
                height: 64,
                bgcolor: "error.main",
                "&:hover": { bgcolor: "error.dark" },
              }}
            >
              <StopIcon sx={{ fontSize: 36, color: "white" }} />
            </IconButton>
          ) : (
            <IconButton
              onClick={startRecording}
              color="primary"
              disabled={!selectedDeviceId}
              sx={{
                width: 64,
                height: 64,
                bgcolor: "primary.main",
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              <MicIcon sx={{ fontSize: 36, color: "white" }} />
            </IconButton>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* AJUSTE RESPONSIVO: Reduz o gap e o minWidth dos timers em telas pequenas */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{ minWidth: { xs: 40, sm: 50 }, textAlign: "center" }}
            >
              {formatTime(currentTime)}
            </Typography>
            <Slider
              value={Math.min(currentTime, sliderMax)}
              min={0}
              max={sliderMax}
              onChange={handleSliderChange}
              disabled={!audioLoaded}
              aria-label="Progresso do áudio"
            />
            <Typography
              sx={{ minWidth: { xs: 40, sm: 50 }, textAlign: "center" }}
            >
              {formatTime(
                duration > 0 && isFinite(duration) ? duration : recordingTime
              )}
            </Typography>
          </Box>

          {/* AJUSTE RESPONSIVO: Altera a direção do flex para 'column' em telas 'xs' */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {isPlaying ? (
              <Button
                fullWidth
                onClick={pauseAudio}
                variant="outlined"
                startIcon={<PauseIcon />}
              >
                Pausar
              </Button>
            ) : (
              <Button
                fullWidth
                onClick={playAudio}
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                disabled={!audioLoaded}
              >
                {audioLoaded ? "Reproduzir" : "Carregando..."}
              </Button>
            )}
            <Button
              fullWidth
              onClick={deleteRecording}
              variant="outlined"
              startIcon={<DeleteIcon />}
              color="error"
            >
              Nova Gravação
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AudioRecorderAnswer;
